import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Fragment,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { usePrivy } from "@privy-io/react-auth";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import ResourceBar from "@/components/ResourceBar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import useEmblaCarousel from "embla-carousel-react";
import { cn, generateTextShadow } from "@/lib/utils";
import { useInventory, useStats } from "@/lib/hooks/useApi";
import type { InventoryItem as ApiInventoryItem } from "@/lib/types/api";
import { Spinner } from "@/components/ui/spinner";
import { MODEL_REGISTRY, type ModelType } from "@/components/RocketsOverlay";
import { Canvas } from "@react-three/fiber";
import { OrthographicCamera } from "@react-three/drei";
import * as THREE from "three";
import { ItemIcon } from "../shop/-components/ItemIcon";
import {
  translateInventoryItemName,
  translateInventoryItemSubtitle,
  translateRarityName,
} from "@/lib/inventoryTranslations";
import { t } from "@/lib/i18n";
import { InventoryDialog } from "./-components/InventoryDialog";

export const Route = createFileRoute("/(game)/inventory/")({
  component: Inventory,
  validateSearch: (search: Record<string, unknown>): { tab?: string } => {
    return {
      tab: typeof search.tab === "string" ? search.tab : undefined,
    };
  },
});

function Inventory() {
  const navigate = useNavigate();
  const { user, login } = usePrivy();
  const walletAddress = user?.wallet?.address;
  const { tab } = Route.useSearch();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ApiInventoryItem | null>(
    null,
  );
  const [activeTab, setActiveTab] = useState(tab || "all");
  const [activeSkinTab, setActiveSkinTab] = useState("chart-frame");
  const [emblaMainRef, emblaMainApi] = useEmblaCarousel();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const hasSyncedFromUrl = useRef(false);

  const skinTypes = ["chart-frame", "chart-line"];

  const getSkinLabel = (type: string) => {
    if (type === "chart-frame") return t("inventory.matchFrame");
    if (type === "chart-line") return t("inventory.matchLine");
    return type;
  };

  // Sync tabs with carousel
  const onSkinTabChange = useCallback(
    (value: string) => {
      setActiveSkinTab(value);
      const index = skinTypes.indexOf(value);
      if (index !== -1 && emblaMainApi) {
        emblaMainApi.scrollTo(index);
      }
    },
    [emblaMainApi, skinTypes],
  );

  // Sync carousel with tabs
  const onSelect = useCallback(() => {
    if (!emblaMainApi) return;
    const index = emblaMainApi.selectedScrollSnap();
    if (skinTypes[index]) {
      setActiveSkinTab(skinTypes[index]);
    }
  }, [emblaMainApi, skinTypes]);

  useEffect(() => {
    if (!emblaMainApi) return;
    onSelect();
    emblaMainApi.on("select", onSelect).on("reInit", onSelect);
  }, [emblaMainApi, onSelect]);

  // Sync tab from URL params
  useEffect(() => {
    if (tab) {
      setActiveTab(tab);
    }
  }, [tab]);

  const { data: userStats } = useStats(walletAddress ?? "");
  const userId = userStats?.userId;
  const {
    data: inventoryData,
    isLoading,
    error,
    refetch: refetchInventory,
  } = useInventory({
    userId,
  });

  const inventoryItems = useMemo(() => {
    if (!inventoryData?.pages) return [];
    return inventoryData.pages.flatMap((page) => page.items);
  }, [inventoryData]);

  const rarityList = useMemo(() => {
    if (!inventoryData?.pages || inventoryData.pages.length === 0) return [];
    // Get rarity list from first page (should be same across all pages)
    const rarities = inventoryData.pages[0].rarity || [];

    // Sort from legend to common
    const rarityOrder = ["legendary", "epic", "rare", "uncommon", "common"];
    return [...rarities].sort((a, b) => {
      const aIndex = rarityOrder.indexOf(a.name?.toLowerCase() || "");
      const bIndex = rarityOrder.indexOf(b.name?.toLowerCase() || "");
      // If not found in order, put at end
      if (aIndex === -1 && bIndex === -1) return 0;
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
  }, [inventoryData]);

  const handleOpenModal = (item: ApiInventoryItem) => {
    setSelectedItem(item);
    setIsSheetOpen(true);
  };

  // Show empty state if user is not logged in
  if (!user || !walletAddress) {
    return (
      <Fragment>
        <div className="relative flex flex-col h-full bg-[#1D2A52] px-2">
          <FloatingLeftButton
            onClick={() => navigate({ to: "/home" })}
            className="absolute left-0 top-4 z-50"
            icon={
              <svg
                className="w-4 h-4"
                viewBox="0 0 9 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
                  fill="white"
                />
              </svg>
            }
          />
          <ResourceBar
            className="absolute top-4.5 left-14 flex z-10 pl-3 space-x-4"
            showBalance
            showBadge={false}
          />

          {/* Header */}
          <div className="w-full px-2 py-2 border-0 flex flex-col items-center mt-12">
            <span
              className="text-white font-black text-2xl text-center"
              style={{
                textShadow: generateTextShadow(1.5),
              }}
            >
              {t("inventory.inventoryTitle")}
            </span>
          </div>

          {/* Empty State */}
          <div className="flex-1 flex flex-col items-center justify-center gap-4 px-4">
            <div className="flex flex-col items-center justify-center">
              <p
                className="text-white text-lg text-center"
                style={{
                  textShadow: generateTextShadow(1.2),
                }}
              >
                {t("inventory.pleaseLoginToViewInventory")}
              </p>
            </div>
            <button
              className="default-btn-container h-8 flex items-center justify-center w-fit pt-2.5! outline-none cursor-pointer"
              style={{
                borderWidth: "2px 2px 2px 2px",
              }}
              onClick={login}
            >
              <img
                src="https://cdn.navalabs.io/icons/login-icon_xgs5dr.png"
                alt="login icon"
                className="size-6 uppercase"
              />
              <span className="uppercase">
                {t("inventory.loginToContinue")}
              </span>
            </button>
          </div>
        </div>
      </Fragment>
    );
  }

  const renderItemGrid = (items: ApiInventoryItem[]) => (
    <div className="grid grid-cols-3 gap-2 w-full">
      {items.map((item) => {
        const isOwned = item.isOwned;
        const isActive = (item as ApiInventoryItem & { isActive?: boolean })
          .isActive;
        const isClaimed = (item as ApiInventoryItem & { isClaimed?: boolean })
          .isClaimed;

        return (
          <button
            key={item.id}
            onClick={() => handleOpenModal(item)}
            className={cn(
              "brown-container relative flex flex-col items-center w-full aspect-square cursor-pointer overflow-visible py-2",
              !isOwned && "grayscale",
            )}
          >
            {/* Claimed Ribbon - Only for gacha items */}
            {isClaimed && item.category === "gacha" && (
              <div
                className="absolute -top-2 -right-2 z-20 h-10 w-10"
                style={{ filter: "none" }}
              >
                <img src="/assets/ribbon-green.png" alt="Claimed" />
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-[10px] -mt-3 font-bold leading-none w-full">
                  <span
                    className="text-white"
                    style={{ textShadow: generateTextShadow(1.2) }}
                  >
                    {t("inventory.claimed")}
                  </span>
                </div>
              </div>
            )}
            {/* Icon - centered */}
            <div className="flex-1 flex items-center justify-center pointer-events-none">
              {Object.keys(MODEL_REGISTRY).includes(item.id) ? (
                (() => {
                  const ModelComponent = MODEL_REGISTRY[item.id as ModelType];
                  return (
                    <div className="w-full h-12 overflow-auto object-cover pointer-events-none">
                      <Canvas style={{ pointerEvents: "none" }}>
                        <OrthographicCamera
                          makeDefault
                          position={[0, 0, 1000]}
                          zoom={1}
                        />
                        <ambientLight position={[0, 0, 10]} intensity={3} />
                        <directionalLight
                          position={[0, THREE.MathUtils.degToRad(40), 0]}
                          intensity={12}
                        />
                        <ModelComponent rotation={0} />
                      </Canvas>
                    </div>
                  );
                })()
              ) : (
                <ItemIcon
                  item={item}
                  className={cn(
                    item.name === "Trophy" ? "w-10" : "w-12",
                    "h-auto object-cover pointer-events-none",
                  )}
                  userProfilePicture={userStats?.profilePicture}
                />
              )}
            </div>

            {/* Name and Subtitle - at bottom */}
            <div className="w-full flex flex-col items-center justify-center gap-0.5 px-2">
              <span
                className="text-white text-xs font-black uppercase text-center line-clamp-2 wrap-break-word"
                style={{ textShadow: generateTextShadow(1.25) }}
              >
                {translateInventoryItemName(item.name, item.shopId, item.id)}
              </span>
              <span className="text-white/70 text-[10px] text-center leading-tight line-clamp-1 wrap-break-word">
                {translateInventoryItemSubtitle(
                  item.subtitle,
                  item.shopId,
                  item.id,
                )}
              </span>
              {item.category !== "cosmetics" &&
                item.quantity !== undefined &&
                item.quantity > 0 && (
                  <span className="text-white/80 text-[10px] font-bold">
                    x{item.quantity}
                  </span>
                )}
              {isActive && (
                <span className="text-green-400 text-[10px] font-bold">
                  {t("inventory.equipped")}
                </span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );

  const renderSectionsByCategory = (items: ApiInventoryItem[]) => {
    return (
      <div className="flex flex-col gap-6">
        {/* Consumables Section */}
        {(() => {
          const consumablesItems = items.filter(
            (item) => item.category === "consumables",
          );
          if (consumablesItems.length === 0) return null;

          return (
            <div className="flex flex-col gap-2 w-full">
              <div className="w-full px-2">
                <span
                  className="uppercase text-white font-black text-lg"
                  style={{
                    textShadow: generateTextShadow(1.2),
                  }}
                >
                  {t("inventory.consumables")}
                </span>
              </div>
              {renderItemGrid(consumablesItems)}
            </div>
          );
        })()}

        {/* Frame Section */}
        {(() => {
          const frameItems = items.filter(
            (item) =>
              item.category === "cosmetics" && item.itemType === "frame",
          );
          if (frameItems.length === 0) return null;

          return (
            <div className="flex flex-col gap-2 w-full">
              <div className="w-full px-2">
                <span
                  className="uppercase text-white font-black text-lg"
                  style={{
                    textShadow: generateTextShadow(1.2),
                  }}
                >
                  {t("inventory.frame")}
                </span>
              </div>
              {renderItemGrid(frameItems)}
            </div>
          );
        })()}

        {/* Match Frame Section */}
        {(() => {
          const matchFrameItems = items.filter(
            (item) =>
              item.category === "cosmetics" && item.itemType === "chart-frame",
          );
          if (matchFrameItems.length === 0) return null;

          return (
            <div className="flex flex-col gap-2 w-full">
              <div className="w-full px-2">
                <span
                  className="uppercase text-white font-black text-lg"
                  style={{
                    textShadow: generateTextShadow(1.2),
                  }}
                >
                  {t("inventory.matchFrame")}
                </span>
              </div>
              {renderItemGrid(matchFrameItems)}
            </div>
          );
        })()}

        {/* Match Line Section */}
        {(() => {
          const matchLineItems = items.filter(
            (item) =>
              item.category === "cosmetics" && item.itemType === "chart-line",
          );
          if (matchLineItems.length === 0) return null;

          return (
            <div className="flex flex-col gap-2 w-full">
              <div className="w-full px-2">
                <span
                  className="uppercase text-white font-black text-lg"
                  style={{
                    textShadow: generateTextShadow(1.2),
                  }}
                >
                  {t("inventory.matchLine")}
                </span>
              </div>
              {renderItemGrid(matchLineItems)}
            </div>
          );
        })()}

        {/* Gacha Section */}
        {(() => {
          const gachaItems = items.filter((item) => item.category === "gacha");
          if (gachaItems.length === 0) return null;

          return (
            <div className="flex flex-col gap-2 w-full">
              <div className="w-full px-2">
                <span
                  className="uppercase text-white font-black text-lg"
                  style={{
                    textShadow: generateTextShadow(1.2),
                  }}
                >
                  {t("inventory.gacha")}
                </span>
              </div>
              {renderItemGrid(gachaItems)}
            </div>
          );
        })()}
      </div>
    );
  };

  const renderSections = (items: ApiInventoryItem[]) => {
    return (
      <div className="flex flex-col gap-6">
        {/* Dynamic rarity sections */}
        {rarityList.map((rarityInfo) => {
          const rarityItems = items.filter(
            (item) => item.rarity === rarityInfo.name,
          );

          if (rarityItems.length === 0) return null;

          return (
            <div key={rarityInfo.name} className="flex flex-col gap-2 w-full">
              <div className="w-full px-2">
                <span
                  className="text-white font-black text-lg"
                  style={{
                    textShadow: generateTextShadow(1.2),
                  }}
                >
                  {translateRarityName(rarityInfo.name)}
                </span>
              </div>
              {renderItemGrid(rarityItems)}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Fragment>
      <div className="relative flex flex-col h-full bg-[#1D2A52] px-2">
        <FloatingLeftButton
          onClick={() => navigate({ to: "/home" })}
          className="absolute left-0 top-4 z-50"
          icon={
            <svg
              className="w-4 h-4"
              viewBox="0 0 9 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
                fill="white"
              />
            </svg>
          }
        />
        <ResourceBar
          className="absolute top-4.5 left-14 flex z-10 pl-3 space-x-4"
          showMenu={false}
          showBadge={false}
        />

        <button
          onClick={() => navigate({ to: "/shop" })}
          className="dark-blue-elevated-container border-4 absolute right-4 top-4 z-50 h-9 flex items-center justify-between"
          style={{
            borderWidth: "2px 2px 2px 2px",
          }}
        >
          <img
            src={`https://cdn.navalabs.io/components/shop/shop-icon_6jx9zo.png`}
            alt={"Shop Icon"}
            className="size-6"
          />
          <span
            className="uppercase text-white text-sm font-black ml-1.5"
            style={{
              textShadow: generateTextShadow(1),
            }}
          >
            {t("inventory.shop")}
          </span>
        </button>

        {/* Header */}
        <div className="w-full px-2 py-2 border-0 flex flex-col items-center mt-12">
          <span
            className="uppercase text-white font-black text-2xl text-center"
            style={{
              textShadow: generateTextShadow(1.5),
            }}
          >
            {t("inventory.inventoryTitle")}
          </span>
        </div>

        {isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <Spinner />
          </div>
        ) : error || !inventoryItems ? (
          <div className="flex-1 flex items-center justify-center text-zinc-400">
            {t("inventory.failedToLoadInventoryItems")}
          </div>
        ) : (
          <div className="flex flex-col w-full flex-1 min-h-0">
            {/* Tabs and Inventory Content */}
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full flex-1 min-h-0 p-0!"
            >
              <TabsList className="w-full">
                <TabsTrigger value="all">{t("inventory.all")}</TabsTrigger>
                <TabsTrigger value="frame">{t("inventory.frame")}</TabsTrigger>
                <TabsTrigger value="consumables">
                  {t("inventory.consumables")}
                </TabsTrigger>
                <TabsTrigger value="skin">{t("inventory.skin")}</TabsTrigger>
                <TabsTrigger value="lucky-draw">
                  {t("inventory.luckyDraw")}
                </TabsTrigger>
                <TabsTrigger value="gacha">{t("inventory.gacha")}</TabsTrigger>
              </TabsList>

              <TabsContent
                value="all"
                className="flex-1 min-h-0 flex flex-col p-0!"
              >
                <ScrollArea className="flex-1 min-h-0" type="scroll">
                  <div className="p-1">
                    {renderSectionsByCategory(inventoryItems)}
                  </div>
                </ScrollArea>
              </TabsContent>

              <TabsContent
                value="frame"
                className="flex-1 min-h-0 flex flex-col p-0!"
              >
                <ScrollArea className="flex-1 min-h-0" type="scroll">
                  <div className="p-1">
                    {renderSections(
                      inventoryItems.filter(
                        (item) =>
                          item.category === "cosmetics" &&
                          item.itemType === "frame",
                      ),
                    )}
                  </div>
                </ScrollArea>
              </TabsContent>

              <TabsContent
                value="consumables"
                className="flex-1 min-h-0 flex flex-col p-0!"
              >
                <ScrollArea className="flex-1 min-h-0" type="scroll">
                  <div className="p-1">
                    {renderSections(
                      inventoryItems.filter(
                        (item) => item.category === "consumables",
                      ),
                    )}
                  </div>
                </ScrollArea>
              </TabsContent>

              <TabsContent
                value="skin"
                className="flex-1 min-h-0 flex flex-col p-0!"
              >
                <div className="flex-1 min-h-0 flex flex-col">
                  <div className="flex gap-2 w-full mb-2">
                    {skinTypes.map((type) => (
                      <button
                        key={type}
                        onClick={() => onSkinTabChange(type)}
                        className="dark-blue-container px-3 py-1 flex items-center justify-center flex-1"
                      >
                        <span
                          className={cn(
                            "font-black text-sm",
                            activeSkinTab === type
                              ? "text-emerald-400"
                              : "text-white",
                          )}
                          style={{
                            textShadow: generateTextShadow(1.2),
                          }}
                        >
                          {getSkinLabel(type)}
                        </span>
                      </button>
                    ))}
                  </div>
                  {/* Carousel */}
                  <div
                    className="flex-1 min-h-0 overflow-hidden"
                    ref={emblaMainRef}
                  >
                    <div className="flex h-full">
                      {skinTypes.map((type) => {
                        const skinItems = inventoryItems.filter(
                          (item) =>
                            item.category === "cosmetics" &&
                            item.itemType === type,
                        );
                        return (
                          <div
                            key={type}
                            className="flex-[0_0_100%] min-w-0 h-full shrink-0"
                          >
                            <ScrollArea className="h-full" type="scroll">
                              <div className="p-1">
                                {renderSections(skinItems)}
                              </div>
                            </ScrollArea>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent
                value="lucky-draw"
                className="flex-1 min-h-0 flex flex-col p-0!"
              >
                <ScrollArea className="flex-1 min-h-0" type="scroll">
                  <div className="p-1">
                    {(() => {
                      const LUCKY_DRAW_SLOTS = 9;
                      const ownedItems = inventoryItems.filter(
                        (item) =>
                          item.category === "lucky-draw" && item.isOwned,
                      );
                      const placeholderCount = Math.max(
                        0,
                        LUCKY_DRAW_SLOTS - ownedItems.length,
                      );

                      const luckyDrawPlaceholderItem: ApiInventoryItem = {
                        id: "lucky-draw-placeholder",
                        name: "???",
                        subtitle: t("inventory.luckyDraw"),
                        rarity: "",
                        category: "lucky-draw",
                        isOwned: false,
                        isActive: false,
                        isClaimed: false,
                        quantity: 0,
                        imageUrl:
                          "https://cofvgxfezvwkjukmvfwl.supabase.co/storage/v1/object/public/cfl-asset/question-mark-box.png",
                        externalUrl: "",
                        itemData: {},
                        itemType: "" as ApiInventoryItem["itemType"],
                        shopId: "",
                        priceInUsdc: 0,
                        priceBeforeDiscount: 0,
                        description: t(
                          "inventory.luckyDrawPlaceholderDescription",
                        ),
                        createdDate: "",
                        updatedDate: "",
                        videoPreviewUrl: "",
                      };

                      return (
                        <div className="flex flex-col gap-2 w-full">
                          <div className="w-full px-2">
                            <span
                              className="uppercase text-white font-black text-lg"
                              style={{
                                textShadow: generateTextShadow(1.2),
                              }}
                            >
                              {t("inventory.luckyDraw")}
                            </span>
                          </div>
                          <div className="grid grid-cols-3 gap-2 w-full">
                            {ownedItems.map((item) => (
                              <button
                                key={item.id}
                                onClick={() => handleOpenModal(item)}
                                className="brown-container relative flex flex-col items-center w-full aspect-square cursor-pointer overflow-visible py-2"
                              >
                                <div className="flex-1 flex items-center justify-center pointer-events-none">
                                  <ItemIcon
                                    item={item}
                                    className="w-12 h-auto object-cover pointer-events-none"
                                    userProfilePicture={
                                      userStats?.profilePicture
                                    }
                                  />
                                </div>
                                <div className="w-full flex flex-col items-center justify-center gap-0.5 px-2">
                                  <span
                                    className="text-white text-xs font-black uppercase text-center line-clamp-2 wrap-break-word"
                                    style={{
                                      textShadow: generateTextShadow(1.25),
                                    }}
                                  >
                                    {translateInventoryItemName(
                                      item.name,
                                      item.shopId,
                                      item.id,
                                    )}
                                  </span>
                                  <span className="text-white/70 text-[10px] text-center leading-tight line-clamp-1 wrap-break-word">
                                    {translateInventoryItemSubtitle(
                                      item.subtitle,
                                      item.shopId,
                                      item.id,
                                    )}
                                  </span>
                                  {item.quantity !== undefined &&
                                    item.quantity > 0 && (
                                      <span className="text-white/80 text-[10px] font-bold">
                                        x{item.quantity}
                                      </span>
                                    )}
                                </div>
                              </button>
                            ))}
                            {Array.from({ length: placeholderCount }).map(
                              (_, i) => (
                                <button
                                  key={`placeholder-${i}`}
                                  onClick={() =>
                                    handleOpenModal(luckyDrawPlaceholderItem)
                                  }
                                  className="brown-container relative flex flex-col items-center w-full aspect-square py-2 grayscale opacity-60 cursor-pointer"
                                >
                                  <div className="flex-1 flex items-center justify-center">
                                    <img
                                      src="https://cofvgxfezvwkjukmvfwl.supabase.co/storage/v1/object/public/cfl-asset/question-mark-box.png"
                                      alt="Mystery Box"
                                      className="w-12 h-auto object-cover"
                                      style={{ imageRendering: "pixelated" }}
                                    />
                                  </div>
                                  <div className="w-full flex flex-col items-center justify-center gap-0.5 px-2">
                                    <span
                                      className="text-white text-xs font-black uppercase text-center"
                                      style={{
                                        textShadow: generateTextShadow(1.25),
                                      }}
                                    >
                                      ???
                                    </span>
                                  </div>
                                </button>
                              ),
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </ScrollArea>
              </TabsContent>

              <TabsContent
                value="gacha"
                className="flex-1 min-h-0 flex flex-col p-0!"
              >
                <ScrollArea className="flex-1 min-h-0" type="scroll">
                  <div className="p-1">
                    {renderSections(
                      inventoryItems.filter(
                        (item) =>
                          item.category === "gacha" ||
                          (item.isOwned &&
                            item.category === "cosmetics" &&
                            (item.itemType === "chart-frame" ||
                              item.itemType === "chart-line")),
                      ),
                    )}
                  </div>
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </div>
        )}

        {selectedItem && (
          <InventoryDialog
            item={selectedItem}
            isSheetOpen={isSheetOpen}
            onOpenChange={setIsSheetOpen}
            refetchInventory={refetchInventory}
          />
        )}
      </div>
    </Fragment>
  );
}
